// /home-shows — region anchor sections with premium event invitation cards.
// Header, footer, and dropdowns are intentionally untouched.
import { Link } from "react-router-dom";
import {
  ArrowRight,
  MapPin,
  CalendarClock,
  Ticket,
  Sparkles,
  Clock3,
} from "lucide-react";
import Reveal from "@/components/layout/Reveal";

// --- Event artwork (uploaded by client) ---------------------------------
const POSTERS = {
  auckland:
    "https://customer-assets.emergentagent.com/job_aero-launch/artifacts/jhlyqib5_Auckland%20Home%20Show%202026.png",
  waikato:
    "https://customer-assets.emergentagent.com/job_aero-launch/artifacts/hyn54zed_Waikato%20Home%20and%20Garden%20Show%202026.png",
  hawkesBay:
    "https://customer-assets.emergentagent.com/job_aero-launch/artifacts/cnttz60d_Home%20and%20Garden%20Show%20Hawke%27s%20Bay%202026.png",
  manawatu:
    "https://customer-assets.emergentagent.com/job_aero-launch/artifacts/yz7p8fsk_Home%20%26%20Lifestyle%20Show%20Manawatu%202026.png",
  wellington:
    "https://customer-assets.emergentagent.com/job_aero-launch/artifacts/lz01ej76_Home%20and%20Garden%20Show%20Wellington%202026.png",
  canterbury:
    "https://customer-assets.emergentagent.com/job_aero-launch/artifacts/w65cc6z7_Canterbury%20Home%20Show%202026.png",
  ggeAuckland:
    "https://customer-assets.emergentagent.com/job_aero-launch/artifacts/c1a1utus_Go%20Green%20Expo%20-%20Auckland%202026.png",
  ggeChristchurch:
    "https://customer-assets.emergentagent.com/job_aero-launch/artifacts/kbaq9nlp_Go%20Green%20Expo%20-%20Christchurch%202026.png",
  ggeWellington:
    "https://customer-assets.emergentagent.com/job_aero-launch/artifacts/308i4mur_Go%20Green%20Expo%20-%20Wellington%202026.png",
  starHomeShow:
    "https://customer-assets.emergentagent.com/job_aero-launch/artifacts/f2v74xdn_The%20Star%20Home%20Show%20-%20Christchurch%202026.png",
};

// --- Region + event data ------------------------------------------------
// Each region has an array of `shows`. A show with a `poster` renders the
// full invitation-style card; a show with `placeholder: true` renders the
// refined "Details coming soon" card.
const REGIONS = [
  {
    id: "auckland",
    name: "Auckland",
    shows: [
      {
        name: "Auckland Home Show",
        date: "Thu 10 – Sun 13 September 2026",
        venue: "Auckland Showgrounds, Greenlane",
        poster: POSTERS.auckland,
      },
      {
        name: "Go Green Expo Auckland",
        date: "Sat 14 – Sun 15 November 2026",
        venue: "EventFinda Stadium, North Shore",
        poster: POSTERS.ggeAuckland,
      },
    ],
  },
  {
    id: "waikato",
    name: "Waikato",
    shows: [
      {
        name: "Waikato Home & Garden Show",
        date: "Thu 1 – Sun 4 October 2026",
        venue: "Claudelands, Hamilton",
        poster: POSTERS.waikato,
      },
    ],
  },
  {
    id: "bay-of-plenty",
    name: "Bay of Plenty",
    shows: [
      {
        name: "Bay of Plenty event",
        placeholder: true,
        copy: "Bay of Plenty event details are being finalised. Register your interest and we'll let you know when AeroMax is exhibiting near you.",
      },
    ],
  },
  {
    id: "hawkes-bay",
    name: "Hawke's Bay",
    shows: [
      {
        name: "Hawke's Bay Home & Garden Show",
        date: "Fri 28 – Sun 30 August 2026",
        venue: "McLean Park, Napier",
        poster: POSTERS.hawkesBay,
      },
    ],
  },
  {
    id: "manawatu",
    name: "Manawatū",
    shows: [
      {
        name: "Home & Lifestyle Show Manawatū",
        date: "Fri 17 – Sun 19 July",
        venue: null,
        poster: POSTERS.manawatu,
      },
    ],
  },
  {
    id: "wellington",
    name: "Wellington",
    shows: [
      {
        name: "Wellington Home & Garden Show",
        date: "Fri 4 – Sun 6 September 2026",
        venue: "Hnry Stadium, Wellington",
        poster: POSTERS.wellington,
      },
      {
        name: "Go Green Expo Wellington",
        date: "Sat 7 – Sun 8 November 2026",
        venue: "TSB Arena, Wellington",
        poster: POSTERS.ggeWellington,
      },
    ],
  },
  {
    id: "canterbury",
    name: "Canterbury",
    shows: [
      {
        name: "Canterbury Home Show",
        date: "Fri 16 – Sun 18 October 2026",
        venue: "Wolfbrook Arena, Addington",
        poster: POSTERS.canterbury,
      },
      {
        name: "The Star Home Show Christchurch",
        date: "Fri 26 – Sun 28 June 2026",
        venue: "Wolfbrook Arena, Jack Hinton Drive, Addington",
        poster: POSTERS.starHomeShow,
      },
      {
        name: "Go Green Expo Christchurch",
        date: "Sat 8 – Sun 9 August 2026",
        venue: "Wolfbrook Arena",
        poster: POSTERS.ggeChristchurch,
      },
    ],
  },
];

// --- Sections -----------------------------------------------------------
function Hero() {
  return (
    <section className="relative overflow-hidden bg-[#0B132B] text-white">
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[440px] w-[660px] rounded-full bg-[#10B981]/12 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 pt-16 pb-20 sm:pt-20 sm:pb-24">
        <Reveal>
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/15 text-[10px] tracking-[0.22em] uppercase font-semibold text-white/85">
            <Sparkles className="h-3 w-3 text-[#10B981]" /> AeroMax Home Shows
          </span>
        </Reveal>
        <Reveal delay={80}>
          <h1 className="font-heading mt-5 text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.06] max-w-4xl">
            Meet AeroMax at <span className="text-[#10B981]">home shows</span> across NZ.
          </h1>
        </Reveal>
        <Reveal delay={140}>
          <p className="mt-6 text-lg sm:text-xl text-white/80 leading-relaxed max-w-2xl">
            See the NeoPower Black Shield up close, talk to our energy specialists, and learn how a heat pump hot water upgrade fits your home.
          </p>
        </Reveal>
        <Reveal delay={200}>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/contact"
              data-testid="home-shows-cta-rsvp"
              className="inline-flex items-center gap-2 rounded-full bg-[#10B981] hover:bg-[#059669] text-white px-7 py-3.5 font-semibold transition-all hover:shadow-xl hover:shadow-emerald-500/30"
            >
              Let us know you're coming <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/savings-calculator"
              data-testid="home-shows-cta-calc"
              className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/15 backdrop-blur border border-white/20 text-white px-7 py-3.5 font-semibold transition-all"
            >
              Estimate Savings First
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function RegionAnchorNav() {
  return (
    <div className="sticky top-20 z-30 bg-white/85 backdrop-blur-md border-b border-[#E2E8F0]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-2 overflow-x-auto no-scrollbar">
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#475569] shrink-0 pr-2">
          Jump to:
        </span>
        {REGIONS.map((r) => (
          <a
            key={r.id}
            href={`#${r.id}`}
            data-testid={`home-shows-anchor-${r.id}`}
            className="text-[12px] font-semibold text-[#0B132B] hover:text-[#10B981] whitespace-nowrap px-3 py-1.5 rounded-full hover:bg-[#10B981]/8 transition-colors"
          >
            {r.name}
          </a>
        ))}
      </div>
    </div>
  );
}

// --- Premium event invitation card (with poster) -----------------------
function EventCard({ show, regionName }) {
  return (
    <article
      data-testid={`home-shows-event-${show.name.replace(/\s+/g, "-").toLowerCase()}`}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-[#E2E8F0] bg-white shadow-sm hover:shadow-xl hover:shadow-emerald-500/10 hover:border-[#10B981]/35 transition-all"
    >
      {/* Poster — object-contain on a soft neutral panel so nothing is cropped */}
      <div className="relative bg-[#F1F5F9] aspect-[4/5] flex items-center justify-center p-3 sm:p-4">
        <img
          src={show.poster}
          alt={`${show.name} official event poster`}
          loading="lazy"
          className="max-h-full max-w-full object-contain drop-shadow-[0_8px_24px_rgba(11,19,43,0.18)] transition-transform duration-500 group-hover:scale-[1.02]"
        />
        <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur border border-[#E2E8F0] text-[10px] font-bold tracking-[0.18em] uppercase text-[#0B132B]">
          <Ticket className="h-3 w-3 text-[#10B981]" /> AeroMax · {regionName}
        </span>
      </div>

      {/* Detail strip */}
      <div className="flex flex-col flex-1 p-5 sm:p-6">
        <h3 className="font-heading text-lg sm:text-xl font-bold text-[#0B132B] leading-snug">
          {show.name}
        </h3>

        <div className="mt-3 space-y-2 text-sm text-[#475569]">
          {show.date && (
            <div className="flex items-start gap-2">
              <CalendarClock className="h-4 w-4 text-[#10B981] mt-[2px] shrink-0" />
              <span>{show.date}</span>
            </div>
          )}
          {show.venue && (
            <div className="flex items-start gap-2">
              <MapPin className="h-4 w-4 text-[#10B981] mt-[2px] shrink-0" />
              <span>{show.venue}</span>
            </div>
          )}
        </div>

        <div className="mt-auto pt-5 flex flex-wrap gap-2.5">
          <button
            type="button"
            data-testid={`home-shows-plan-visit-${show.name.replace(/\s+/g, "-").toLowerCase()}`}
            className="inline-flex items-center gap-2 rounded-full bg-[#0B132B] hover:bg-[#1E293B] text-white px-4 py-2.5 text-xs sm:text-sm font-semibold transition-all"
          >
            Plan your visit <ArrowRight className="h-3.5 w-3.5" />
          </button>
          <Link
            to="/savings-calculator"
            data-testid={`home-shows-run-calc-${show.name.replace(/\s+/g, "-").toLowerCase()}`}
            className="inline-flex items-center gap-2 rounded-full border-2 border-[#E2E8F0] hover:border-[#0B132B] text-[#0B132B] px-4 py-2.5 text-xs sm:text-sm font-semibold transition-all"
          >
            Run the calculator
          </Link>
        </div>
      </div>
    </article>
  );
}

// --- Refined "Details coming soon" placeholder card --------------------
function PlaceholderCard({ show, regionName }) {
  return (
    <article
      data-testid={`home-shows-placeholder-${show.name.replace(/\s+/g, "-").toLowerCase()}`}
      className="relative flex flex-col overflow-hidden rounded-2xl border border-dashed border-[#E2E8F0] bg-gradient-to-br from-white via-white to-[#F0FDF4] shadow-sm hover:shadow-md hover:border-[#10B981]/40 transition-all"
    >
      <div className="relative bg-[#0B132B] aspect-[4/5] overflow-hidden flex items-center justify-center">
        {/* subtle grid + glow */}
        <div
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)",
            backgroundSize: "32px 32px",
          }}
        />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[220px] w-[260px] rounded-full bg-[#10B981]/14 blur-3xl pointer-events-none" />
        <div className="relative text-center px-6">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 backdrop-blur text-[10px] tracking-[0.22em] uppercase font-bold text-white/85">
            <Clock3 className="h-3 w-3 text-[#10B981]" /> Details coming soon
          </span>
          <p className="font-heading mt-5 text-xl sm:text-2xl font-bold text-white leading-tight">
            {show.name}
          </p>
          <p className="mt-2 text-xs uppercase tracking-[0.18em] font-semibold text-white/55">
            {regionName}
          </p>
        </div>
      </div>

      <div className="flex flex-col flex-1 p-5 sm:p-6">
        <p className="text-sm text-[#475569] leading-relaxed">
          {show.copy ||
            "We'll confirm dates, venue and booth number for this event soon. Register your interest to be notified first."}
        </p>
        <div className="mt-auto pt-5 flex flex-wrap gap-2.5">
          <a
            href="#register-interest"
            data-testid={`home-shows-notify-${show.name.replace(/\s+/g, "-").toLowerCase()}`}
            className="inline-flex items-center gap-2 rounded-full bg-[#0B132B] hover:bg-[#1E293B] text-white px-4 py-2.5 text-xs sm:text-sm font-semibold transition-all"
          >
            Notify me <ArrowRight className="h-3.5 w-3.5" />
          </a>
          <Link
            to="/savings-calculator"
            className="inline-flex items-center gap-2 rounded-full border-2 border-[#E2E8F0] hover:border-[#0B132B] text-[#0B132B] px-4 py-2.5 text-xs sm:text-sm font-semibold transition-all"
          >
            Run the calculator
          </Link>
        </div>
      </div>
    </article>
  );
}

// --- Region block (header + grid of cards) -----------------------------
function RegionBlock({ region }) {
  const count = region.shows.length;
  const isSingle = count === 1;
  // Tri-column on large screens when a region has 3 events (e.g. Canterbury).
  const gridCols =
    count >= 3 ? "sm:grid-cols-2 lg:grid-cols-3" : "sm:grid-cols-2";
  return (
    <section
      id={region.id}
      data-testid={`home-shows-region-${region.id}`}
      className="scroll-mt-32"
    >
      <div className="flex items-end justify-between gap-4 mb-5 sm:mb-6">
        <div>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#10B981]/10 text-[#059669] text-[10px] font-bold tracking-[0.18em] uppercase">
            <MapPin className="h-3 w-3" /> {region.name}
          </span>
          <h2 className="font-heading mt-3 text-2xl sm:text-3xl font-bold text-[#0B132B] leading-tight">
            AeroMax in {region.name}
          </h2>
        </div>
      </div>

      <div className={`grid gap-5 sm:gap-6 ${gridCols}`}>
        {region.shows.map((show, i) =>
          show.placeholder ? (
            <PlaceholderCard key={i} show={show} regionName={region.name} />
          ) : (
            <EventCard key={i} show={show} regionName={region.name} />
          )
        )}
        {/* When there's only one event in a region, balance the grid on desktop
            with a soft "What to expect at the AeroMax stand" panel. */}
        {isSingle && (
          <aside
            aria-hidden="true"
            className="hidden sm:flex relative overflow-hidden rounded-2xl border border-[#E2E8F0] bg-gradient-to-br from-white via-white to-[#F0FDF4] p-7 flex-col justify-between"
          >
            <div>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#0B132B] text-white text-[10px] font-bold tracking-[0.18em] uppercase">
                <Sparkles className="h-3 w-3 text-[#10B981]" /> At our stand
              </span>
              <h3 className="font-heading mt-4 text-xl font-bold text-[#0B132B] leading-snug">
                See the NeoPower Black Shield up close.
              </h3>
              <ul className="mt-4 space-y-2 text-sm text-[#475569]">
                <li className="flex items-start gap-2">
                  <span className="mt-[7px] h-1.5 w-1.5 rounded-full bg-[#10B981] shrink-0" />
                  Talk to certified AeroMax energy specialists
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-[7px] h-1.5 w-1.5 rounded-full bg-[#10B981] shrink-0" />
                  Live demo of the heat pump hot water cylinder
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-[7px] h-1.5 w-1.5 rounded-full bg-[#10B981] shrink-0" />
                  Personalised savings estimate on the spot
                </li>
              </ul>
            </div>
            <div className="mt-6">
              <Link
                to="/savings-calculator"
                className="inline-flex items-center gap-2 rounded-full bg-[#10B981] hover:bg-[#059669] text-white px-5 py-2.5 text-sm font-semibold transition-all"
              >
                Estimate savings first <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            <div className="absolute -right-12 -bottom-12 h-40 w-40 rounded-full bg-[#10B981]/10 blur-2xl pointer-events-none" />
          </aside>
        )}
      </div>
    </section>
  );
}

// --- Page --------------------------------------------------------------
export default function HomeShows() {
  return (
    <div data-testid="home-shows-page" className="bg-white">
      <Hero />
      <RegionAnchorNav />
      <div className="py-14 sm:py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 space-y-14 sm:space-y-20">
          {REGIONS.map((r) => (
            <RegionBlock key={r.id} region={r} />
          ))}

          <div
            id="register-interest"
            data-testid="home-shows-coming-soon"
            className="scroll-mt-32 relative rounded-3xl overflow-hidden border border-[#E2E8F0] bg-gradient-to-br from-white via-white to-[#F0FDF4] p-7 sm:p-10 shadow-sm"
          >
            <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#10B981]/10 blur-2xl pointer-events-none" />
            <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#10B981]">
              Register your interest
            </p>
            <h3 className="font-heading mt-2 text-2xl sm:text-3xl font-bold text-[#0B132B] leading-tight max-w-2xl">
              Want a heads-up when AeroMax is exhibiting near you?
            </h3>
            <p className="mt-3 text-[#475569] max-w-2xl leading-relaxed">
              Drop us a line and we&apos;ll let you know the moment dates are confirmed for your region. The AeroMax home-show schedule is being finalised.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/contact"
                data-testid="home-shows-notify-cta"
                className="inline-flex items-center gap-2 rounded-full bg-[#10B981] hover:bg-[#059669] text-white px-6 py-3 font-semibold transition-all"
              >
                Register Interest <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
